/**
 * IncomingAuditPage
 * 
 * Página para realizar auditoria física de estoque
 * Usuários com grupo "sorteado" podem registrar quantidades contadas
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Package, Save, Loader2, CheckCircle2,
  AlertTriangle, Minus, Plus, MapPin, User, Edit2, ChevronDown,
  Camera, Search, RefreshCw
} from 'lucide-react'
import Avatar from '../../../../components/Avatar/Avatar'
import MobileBarcodeScanner from '../../../../components/ERP/MobileBarcodeScanner/MobileBarcodeScanner'
import { stockAuditService } from '../../../../services/erpApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import { useAuth } from '../../../../contexts/AuthContext'
import { MODULES, ACTIONS } from '../../../../utils/permissions'
import './IncomingAuditPage.css'

interface AuditItem {
  id: number
  product_id: number
  product_sku?: string
  product_name?: string
  product_barcode?: string
  location_code?: string
  expected_quantity: number
  counted_quantity?: number
  audited_quantity?: number // Quantidade salva no backend
  difference?: number
  status: 'pending' | 'counted' | 'divergent' | 'matched'
  notes?: string
  current_stock_system?: number
}

interface AuditData {
  id: number
  audit_number: string
  brand_id: number
  brand_name: string
  status: 'pending' | 'in_progress' | 'completed' | 'applied' | 'cancelled'
  start_date: string
  created_by_name?: string
  responsible_user_id?: number
  responsible_user_name?: string
  items: AuditItem[]
}

interface EligibleUser {
  id: number
  name: string
  email: string
  group_name?: string
}

function IncomingAuditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [zeroingUnaudited, setZeroingUnaudited] = useState(false)
  const [syncingProducts, setSyncingProducts] = useState(false)
  const [updatingResponsible, setUpdatingResponsible] = useState(false)
  const [auditData, setAuditData] = useState<AuditData | null>(null)
  const [items, setItems] = useState<AuditItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[]>([])
  const [showResponsibleEdit, setShowResponsibleEdit] = useState(false)
  const [selectedResponsibleId, setSelectedResponsibleId] = useState<number | null>(null)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null)
  const [isMobileView, setIsMobileView] = useState(false)

  const canEditResponsible = hasPermission(MODULES.STOCK, ACTIONS.STOCK_AUDITS_EDIT_RESPONSIBLE)

  const isItemAuditedOnServer = (item: AuditItem) =>
    item.audited_quantity !== null && item.audited_quantity !== undefined

  const hasUnsavedCount = (item: AuditItem) =>
    item.counted_quantity !== null &&
    item.counted_quantity !== undefined &&
    !isItemAuditedOnServer(item)

  const loadAuditData = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      const response = await stockAuditService.getById(parseInt(id))
      if (response.success && response.data) {
        const data = response.data
        setAuditData(data)
        // Mapear audited_quantity para counted_quantity para compatibilidade
        // IMPORTANTE: Preservar audited_quantity do backend para validação
        const mappedItems = Array.isArray(data.items) ? data.items.map((item: any) => {
          const auditedQty = item.audited_quantity ?? null
          const countedQty = auditedQty ?? item.counted_quantity
          let status: AuditItem['status'] = 'pending'

          if (auditedQty !== null && auditedQty !== undefined) {
            if (item.has_discrepancy === true) {
              status = 'divergent'
            } else {
              status = 'matched'
            }
          }

          return {
            ...item,
            counted_quantity: countedQty,
            audited_quantity: auditedQty,
            status: item.status || status
          }
        }) : []
        setItems(mappedItems)
        setSelectedResponsibleId(data.responsible_user_id || null)
      } else {
        showToast('Auditoria não encontrada', 'error')
        navigate('/erp/stock/audits')
      }
    } catch (error) {
      console.error('Erro ao carregar auditoria:', error)
      showToast('Erro ao carregar auditoria', 'error')
      navigate('/erp/stock/audits')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  const loadEligibleUsers = useCallback(async () => {
    if (!canEditResponsible) return
    
    try {
      const response = await stockAuditService.getEligibleResponsibles()
      if (response.success && response.data) {
        setEligibleUsers(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar usuários elegíveis:', error)
    }
  }, [canEditResponsible])

  useEffect(() => {
    loadAuditData()
    loadEligibleUsers()
  }, [loadAuditData, loadEligibleUsers])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)')
    const updateMobileView = () => {
      setIsMobileView(
        mediaQuery.matches ||
        (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024)
      )
    }

    updateMobileView()
    mediaQuery.addEventListener('change', updateMobileView)
    window.addEventListener('resize', updateMobileView)

    return () => {
      mediaQuery.removeEventListener('change', updateMobileView)
      window.removeEventListener('resize', updateMobileView)
    }
  }, [])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('[data-user-dropdown]')) {
        setShowUserDropdown(false)
      }
    }
    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserDropdown])

  const handleUpdateResponsible = async () => {
    if (!id) return

    try {
      setUpdatingResponsible(true)
      const response = await stockAuditService.updateResponsible(parseInt(id), selectedResponsibleId)
      if (response.success) {
        showToast('Responsável atualizado com sucesso!', 'success')
        setShowResponsibleEdit(false)
        loadAuditData()
      } else {
        showToast(response.message || 'Erro ao atualizar responsável', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao atualizar responsável:', error)
      showToast(error.response?.data?.message || 'Erro ao atualizar responsável', 'error')
    } finally {
      setUpdatingResponsible(false)
    }
  }

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const handleQuantityChange = (itemId: number, value: string) => {
    const numValue = value === '' ? null : Math.max(0, parseInt(value) || 0)
    
    setItems(prevItems => {
      return prevItems.map(item => {
        if (item.id === itemId) {
          const counted = numValue ?? null
          const difference = counted !== null ? counted - item.expected_quantity : null
          let status: AuditItem['status'] = 'pending'
          
          if (counted !== null) {
            if (counted === item.expected_quantity) {
              status = 'matched'
            } else {
              status = 'divergent'
            }
          }
          
          return {
            ...item,
            counted_quantity: counted,
            difference: difference ?? undefined,
            status
          }
        }
        return item
      })
    })
  }

  const handleSaveItem = async (item: AuditItem) => {
    if (item.counted_quantity === null || item.counted_quantity === undefined) {
      showToast('Por favor, informe a quantidade contada', 'warning')
      return
    }

    try {
      setSaving(true)
      if (!id) {
        showToast('ID da auditoria não encontrado', 'error')
        return
      }

      const response = await stockAuditService.recordCount(parseInt(id), item.id, {
        quantity: item.counted_quantity
      })

      if (response.success) {
        showToast('Contagem salva com sucesso!', 'success')
        // Desmarca o item escaneado após salvar a quantidade
        setHighlightedItemId((current) => (current === item.id ? null : current))
        setItems(prevItems =>
          prevItems.map(i => i.id === item.id ? {
            ...i,
            ...response.data,
            counted_quantity: response.data.audited_quantity ?? response.data.counted_quantity,
            audited_quantity: response.data.audited_quantity
          } : i)
        )
      } else {
        showToast(response.message || 'Erro ao salvar contagem', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao salvar contagem:', error)
      showToast(error.response?.data?.message || 'Erro ao salvar contagem', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleZeroUnaudited = async () => {
    if (!id) return

    const pending = getPendingAuditCount()
    if (pending === 0) {
      showToast('Todos os itens já foram auditados', 'warning')
      return
    }

    const confirmed = window.confirm(
      `Registrar ${pending} produto(s) não lido(s) com quantidade 0?\n\nItens não escaneados serão considerados sem estoque físico.`
    )
    if (!confirmed) return

    try {
      setZeroingUnaudited(true)
      const response = await stockAuditService.zeroUnaudited(id)
      if (response.success) {
        showToast(response.message || 'Itens não lidos registrados como 0', 'success')
        await loadAuditData()
      } else {
        showToast(response.message || 'Erro ao registrar itens como 0', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao zerar itens não auditados:', error)
      showToast(error.response?.data?.message || 'Erro ao registrar itens como 0', 'error')
    } finally {
      setZeroingUnaudited(false)
    }
  }

  const handleStartAudit = async () => {
    if (!id) return

    try {
      setSaving(true)
      const response = await stockAuditService.start(parseInt(id))
      if (response.success) {
        showToast('Auditoria iniciada!', 'success')
        loadAuditData()
      } else {
        showToast(response.message || 'Erro ao iniciar auditoria', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao iniciar auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao iniciar auditoria', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSyncProducts = async () => {
    if (!id) return

    try {
      setSyncingProducts(true)
      const response = await stockAuditService.syncProducts(parseInt(id))
      if (response.success) {
        const added = response.data?.addedCount ?? 0
        if (added > 0) {
          showToast(`${added} produto(s) adicionado(s) à auditoria`, 'success')
        } else {
          showToast(response.message || 'Nenhum produto novo da marca para incluir', 'warning')
        }
        await loadAuditData()
      } else {
        showToast(response.message || 'Erro ao atualizar produtos', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar produtos:', error)
      showToast(error.response?.data?.message || 'Erro ao atualizar produtos', 'error')
    } finally {
      setSyncingProducts(false)
    }
  }

  const handleCompleteAudit = async () => {
    if (!id) return

    if (getUnsavedCount() > 0) {
      showToast('Salve as contagens pendentes antes de concluir a auditoria', 'warning')
      return
    }

    const pending = getPendingAuditCount()
    if (pending > 0) {
      const confirmed = window.confirm(
        `Há ${pending} produto(s) não lido(s).\n\nNa contagem por leitura, itens não escaneados serão registrados automaticamente como quantidade 0 (sem estoque). Deseja concluir a auditoria?`
      )
      if (!confirmed) return
    }

    try {
      setCompleting(true)
      const response = await stockAuditService.complete(parseInt(id), {
        assumeZeroForMissing: pending > 0
      })
      if (response.success) {
        showToast('Auditoria concluída com sucesso!', 'success')
        setTimeout(() => {
          navigate('/erp/stock/audits')
        }, 1500)
      } else {
        showToast(response.message || 'Erro ao concluir auditoria', 'error')
        const pendingItems = items.filter(item => !isItemAuditedOnServer(item))
        if (pendingItems.length > 0) {
          setHighlightedItemId(pendingItems[0].id)
          window.setTimeout(() => {
            document.getElementById(`audit-item-row-${pendingItems[0].id}`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }, 200)
        }
      }
    } catch (error: any) {
      console.error('Erro ao concluir auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao concluir auditoria', 'error')
    } finally {
      setCompleting(false)
    }
  }

  const getProgress = () => {
    if (!items.length) return 0
    const counted = items.filter(item => isItemAuditedOnServer(item)).length
    return Math.round((counted / items.length) * 100)
  }

  const getPendingAuditCount = () => {
    return items.filter(item => !isItemAuditedOnServer(item)).length
  }

  const getUnsavedCount = () => {
    return items.filter(item => hasUnsavedCount(item)).length
  }

  const findItemByCode = (code: string) => {
    const normalized = code.trim().toLowerCase()
    if (!normalized) return null

    return items.find(item =>
      item.product_barcode?.toLowerCase() === normalized ||
      item.product_sku?.toLowerCase() === normalized ||
      String(item.product_id) === normalized
    ) || null
  }

  const handleBarcodeScan = (code: string) => {
    const item = findItemByCode(code)

    if (!item) {
      showToast(`Produto não encontrado nesta auditoria: ${code}`, 'warning')
      setSearchTerm(code)
      return
    }

    setSearchTerm('')
    setHighlightedItemId(item.id)
    showToast(`Produto encontrado: ${item.product_name || item.product_sku || code}`, 'success')

    window.setTimeout(() => {
      document.getElementById(`audit-item-row-${item.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }, 100)
  }

  const getDivergentCount = () => {
    return items.filter(item => item.status === 'divergent').length
  }

  const filteredItems = items.filter(item => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      item.product_name?.toLowerCase().includes(search) ||
      item.product_sku?.toLowerCase().includes(search) ||
      item.product_barcode?.toLowerCase().includes(search) ||
      item.product_id.toString().includes(search)
    )
  })

  if (loading) {
    return (
      <div className="incoming-audit-loading">
        <Loader2 size={32} className="incoming-audit-spinner" />
        <p>Carregando auditoria...</p>
      </div>
    )
  }

  if (!auditData) {
    return null
  }

  const canEdit = auditData.status === 'in_progress'
  const canStart = auditData.status === 'pending'
  const progress = getProgress()
  const divergentCount = getDivergentCount()
  const pendingAuditCount = getPendingAuditCount()
  const unsavedCount = getUnsavedCount()
  
  // Verificar se o usuário logado é o responsável pela auditoria
  const isResponsible = user && auditData.responsible_user_id 
    ? user.id === auditData.responsible_user_id 
    : false
  
  // Permitir edição apenas se for o responsável ou admin
  // Se não houver responsável definido, apenas admin pode editar
  const canEditQuantities = canEdit && (
    isAdmin() || 
    (isResponsible && auditData.responsible_user_id !== null)
  )

  return (
    <div className="incoming-audit">
      {/* Header */}
      <div className="incoming-audit-header">
        <div className="incoming-audit-header-left">
          <button 
            onClick={() => navigate('/erp/stock/audits')} 
            className="incoming-audit-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="incoming-audit-title">
              Auditoria {auditData.audit_number}
            </h1>
            <p className="incoming-audit-subtitle">
              Marca: {auditData.brand_name} • {items.length} itens
              {auditData.responsible_user_name && (
                <> • Responsável: {auditData.responsible_user_name}</>
              )}
            </p>
          </div>
        </div>

        <div className="incoming-audit-header-right">
          {canEditResponsible && (
            <div style={{ position: 'relative', marginRight: '1rem' }}>
              {!showResponsibleEdit ? (
                <button
                  onClick={() => setShowResponsibleEdit(true)}
                  className="incoming-audit-btn-secondary"
                >
                  <User size={16} />
                  {auditData.responsible_user_name || 'Sem responsável'}
                  <Edit2 size={14} />
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                  <div className="incoming-audit-user-dropdown" data-user-dropdown>
                    <button
                      type="button"
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      className="incoming-audit-user-dropdown-button"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        {selectedResponsibleId ? (
                          <>
                            {(() => {
                              const selectedUser = eligibleUsers.find(u => u.id === selectedResponsibleId)
                              return selectedUser ? (
                                <>
                                  <Avatar name={selectedUser.name} email={selectedUser.email || ''} imageUrl={selectedUser.avatar_url} size="sm" />
                                  <span style={{ fontSize: '0.875rem' }}>
                                    {selectedUser.name} {selectedUser.group_name ? `(${selectedUser.group_name})` : ''}
                                  </span>
                                </>
                              ) : (
                                <span style={{ fontSize: '0.875rem' }}>Sem responsável</span>
                              )
                            })()}
                          </>
                        ) : (
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Sem responsável</span>
                        )}
                      </div>
                      <ChevronDown size={16} style={{ color: '#6b7280', transform: showUserDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                    </button>
                    {showUserDropdown && (
                      <div className="incoming-audit-user-dropdown-menu">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedResponsibleId(null)
                            setShowUserDropdown(false)
                          }}
                          className={`incoming-audit-user-dropdown-option ${selectedResponsibleId === null ? 'active' : ''}`}
                        >
                          <span style={{ color: 'var(--text-tertiary)' }}>Sem responsável</span>
                        </button>
                        {eligibleUsers.length === 0 ? (
                          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
                            Nenhum usuário elegível encontrado
                          </div>
                        ) : (
                          eligibleUsers.map(user => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setSelectedResponsibleId(user.id)
                                setShowUserDropdown(false)
                              }}
                              className={`incoming-audit-user-dropdown-option ${selectedResponsibleId === user.id ? 'active' : ''}`}
                            >
                              <Avatar name={user.name} email={user.email || ''} imageUrl={user.avatar_url} size="sm" />
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontWeight: 500 }}>{user.name}</span>
                                {user.group_name && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{user.group_name}</span>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleUpdateResponsible}
                    disabled={updatingResponsible}
                    className="incoming-audit-btn-primary"
                  >
                    {updatingResponsible ? <Loader2 size={14} className="incoming-audit-spinner" /> : 'Salvar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowResponsibleEdit(false)
                      setSelectedResponsibleId(auditData.responsible_user_id || null)
                      setShowUserDropdown(false)
                    }}
                    className="incoming-audit-btn-tertiary"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}
          {auditData.status === 'pending' && (
            <button 
              onClick={handleStartAudit}
              disabled={saving}
              className="incoming-audit-btn-primary"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="incoming-audit-spinner" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Package size={16} />
                  Iniciar Auditoria
                </>
              )}
            </button>
          )}
          {(auditData.status === 'pending' || auditData.status === 'in_progress') &&
            (auditData.brand_id || auditData.brand_name) && (
            <button
              type="button"
              onClick={handleSyncProducts}
              disabled={syncingProducts}
              className="incoming-audit-btn-secondary"
              title="Inclui produtos da marca que ainda não estão nesta auditoria (ex.: marca corrigida depois)"
            >
              {syncingProducts ? (
                <>
                  <Loader2 size={16} className="incoming-audit-spinner" />
                  Atualizando...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Atualizar produtos
                </>
              )}
            </button>
          )}
          {canEdit && auditData.status === 'in_progress' && canEditQuantities && pendingAuditCount > 0 && (
            <button
              onClick={handleZeroUnaudited}
              disabled={zeroingUnaudited || unsavedCount > 0}
              className="incoming-audit-btn-secondary"
            >
              {zeroingUnaudited ? (
                <>
                  <Loader2 size={16} className="incoming-audit-spinner" />
                  Registrando...
                </>
              ) : (
                <>
                  <Minus size={16} />
                  Não lidos → 0 ({pendingAuditCount})
                </>
              )}
            </button>
          )}
          {canEdit && auditData.status === 'in_progress' && canEditQuantities && (
            <button 
              onClick={handleCompleteAudit}
              disabled={completing || unsavedCount > 0}
              className="incoming-audit-btn-primary"
            >
              {completing ? (
                <>
                  <Loader2 size={16} className="incoming-audit-spinner" />
                  Finalizando...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Concluir Auditoria
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Aviso se não for o responsável */}
      {canEdit && !canEditQuantities && !isAdmin() && (
        <div className="incoming-audit-warning" style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '0.5rem',
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertTriangle size={20} />
          <div>
            <strong>Apenas o responsável pela auditoria pode adicionar quantidades.</strong>
            {auditData.responsible_user_name ? (
              <span style={{ display: 'block', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                Responsável: {auditData.responsible_user_name}
              </span>
            ) : (
              <span style={{ display: 'block', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                Nenhum responsável definido para esta auditoria.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {auditData.status === 'in_progress' && (
        <div className="incoming-audit-progress-section">
          <div className="incoming-audit-progress-header">
            <span className="incoming-audit-progress-label">
              Progresso: {progress}%
            </span>
            {divergentCount > 0 && (
              <span className="incoming-audit-divergent-count">
                <AlertTriangle size={14} />
                {divergentCount} divergência(s)
              </span>
            )}
            {pendingAuditCount > 0 && (
              <span className="incoming-audit-pending-count">
                <AlertTriangle size={14} />
                {pendingAuditCount} não auditado(s)
              </span>
            )}
            {unsavedCount > 0 && (
              <span className="incoming-audit-unsaved-count">
                <Save size={14} />
                {unsavedCount} não salvo(s)
              </span>
            )}
          </div>
          <div className="incoming-audit-progress-bar">
            <div 
              className="incoming-audit-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Search */}
      <div className="incoming-audit-search">
        <div className="incoming-audit-search-wrapper">
          <Search size={18} className="incoming-audit-search-icon" />
          <input
            type="text"
            placeholder="Buscar produto por nome, SKU, código de barras ou ID..."
            className="incoming-audit-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {auditData.status === 'in_progress' && canEditQuantities && (
        <div className="incoming-audit-scan-hint">
          <Package size={18} />
          <div>
            <strong>Contagem por leitura</strong>
            <span>
              Use o scanner para localizar o produto na lista, informe a quantidade manualmente
              e salve. Itens não auditados podem ser registrados como 0 ao concluir ou pelo
              botão &quot;Não lidos → 0&quot;.
            </span>
          </div>
        </div>
      )}

      {pendingAuditCount > 0 && auditData.status === 'in_progress' && (
        <div className="incoming-audit-pending-banner">
          <AlertTriangle size={18} />
          <div>
            <strong>{pendingAuditCount} item(ns) ainda não auditado(s).</strong>
            <span>Use o scanner para localizar produtos. Informe a quantidade e salve cada item auditado.</span>
          </div>
        </div>
      )}

      {/* Items — cards no mobile (2 linhas), tabela no desktop */}
      <div className="incoming-audit-card">
        {isMobileView ? (
          <div className="incoming-audit-mobile-list">
            {filteredItems.length === 0 ? (
              <div className="incoming-audit-empty">
                <p>Nenhum item encontrado</p>
              </div>
            ) : (
              filteredItems.map((item) => {
                const isCounted =
                  item.counted_quantity !== null &&
                  item.counted_quantity !== undefined
                const auditedOnServer = isItemAuditedOnServer(item)
                const unsaved = hasUnsavedCount(item)
                const cardClassName = [
                  'incoming-audit-mobile-card',
                  !auditedOnServer ? 'incoming-audit-mobile-card--pending' : '',
                  unsaved ? 'incoming-audit-mobile-card--unsaved' : '',
                  highlightedItemId === item.id
                    ? 'incoming-audit-mobile-card--highlight'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')

                return (
                  <article
                    key={item.id}
                    id={`audit-item-row-${item.id}`}
                    className={cardClassName}
                  >
                    <div className="incoming-audit-mobile-card-line1">
                      <div className="incoming-audit-mobile-card-main">
                        <span className="incoming-audit-product-name">
                          {item.product_name || `Produto #${item.product_id}`}
                        </span>
                        <div className="incoming-audit-mobile-card-meta">
                          {item.location_code ? (
                            <span className="incoming-audit-location-badge">
                              <MapPin size={12} />
                              {item.location_code}
                            </span>
                          ) : null}
                          {item.product_sku ? (
                            <span className="incoming-audit-product-sku">
                              SKU: {item.product_sku}
                            </span>
                          ) : null}
                          {item.product_barcode ? (
                            <span className="incoming-audit-barcode">
                              {item.product_barcode}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="incoming-audit-mobile-card-badges">
                        {!auditedOnServer && (
                          <span className="incoming-audit-status-badge incoming-audit-status-badge--pending">
                            Não auditado
                          </span>
                        )}
                        {unsaved && (
                          <span className="incoming-audit-status-badge incoming-audit-status-badge--unsaved">
                            Não salvo
                          </span>
                        )}
                        {auditedOnServer && item.status === 'divergent' && (
                          <span className="incoming-audit-status-badge incoming-audit-status-badge--divergent">
                            Divergente
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="incoming-audit-mobile-card-line2">
                      {canEdit ? (
                        canEditQuantities ? (
                          <div className="incoming-audit-quantity-input-wrapper">
                            <button
                              type="button"
                              className="incoming-audit-qty-btn"
                              onClick={() =>
                                handleQuantityChange(
                                  item.id,
                                  String((item.counted_quantity || 0) - 1)
                                )
                              }
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              min="0"
                              inputMode="numeric"
                              className="incoming-audit-quantity-input"
                              value={item.counted_quantity ?? ''}
                              onChange={(e) =>
                                handleQuantityChange(item.id, e.target.value)
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                              placeholder="0"
                              aria-label="Quantidade contada"
                            />
                            <button
                              type="button"
                              className="incoming-audit-qty-btn"
                              onClick={() =>
                                handleQuantityChange(
                                  item.id,
                                  String((item.counted_quantity || 0) + 1)
                                )
                              }
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        ) : (
                          <span className="incoming-audit-quantity-badge incoming-audit-quantity-disabled">
                            Apenas responsável
                          </span>
                        )
                      ) : canStart ? (
                        <span className="incoming-audit-quantity-badge incoming-audit-quantity-disabled">
                          Inicie a auditoria
                        </span>
                      ) : (
                        <span className="incoming-audit-quantity-badge">
                          {item.counted_quantity ?? '-'}
                        </span>
                      )}

                      {canEditQuantities && isCounted ? (
                        <button
                          type="button"
                          className="incoming-audit-save-btn"
                          onClick={() => handleSaveItem(item)}
                          disabled={saving}
                          aria-label="Salvar contagem"
                        >
                          {saving ? (
                            <Loader2 size={14} className="incoming-audit-spinner" />
                          ) : (
                            <Save size={14} />
                          )}
                          <span>Salvar</span>
                        </button>
                      ) : null}
                    </div>
                  </article>
                )
              })
            )}
          </div>
        ) : (
          <div className="incoming-audit-table-wrapper">
            <table className="incoming-audit-table">
              <thead>
                <tr>
                  <th>Endereço</th>
                  <th>Produto</th>
                  <th>Código de Barras</th>
                  <th className="text-center">Quantidade</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="incoming-audit-empty">
                      <p>Nenhum item encontrado</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const isCounted =
                      item.counted_quantity !== null &&
                      item.counted_quantity !== undefined
                    const auditedOnServer = isItemAuditedOnServer(item)
                    const unsaved = hasUnsavedCount(item)
                    const rowClassName = [
                      'incoming-audit-table-row',
                      !auditedOnServer ? 'incoming-audit-table-row--pending' : '',
                      unsaved ? 'incoming-audit-table-row--unsaved' : '',
                      highlightedItemId === item.id
                        ? 'incoming-audit-table-row--highlight'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')

                    return (
                      <tr
                        key={item.id}
                        id={`audit-item-row-${item.id}`}
                        className={rowClassName}
                      >
                        <td>
                          {item.location_code ? (
                            <span className="incoming-audit-location-badge">
                              <MapPin size={14} />
                              {item.location_code}
                            </span>
                          ) : (
                            <span className="incoming-audit-location-empty">N/A</span>
                          )}
                        </td>
                        <td>
                          <div className="incoming-audit-product-info">
                            <div className="incoming-audit-product-title-row">
                              <span className="incoming-audit-product-name">
                                {item.product_name || `Produto #${item.product_id}`}
                              </span>
                              {!auditedOnServer && (
                                <span className="incoming-audit-status-badge incoming-audit-status-badge--pending">
                                  <AlertTriangle size={12} />
                                  Não auditado
                                </span>
                              )}
                              {unsaved && (
                                <span className="incoming-audit-status-badge incoming-audit-status-badge--unsaved">
                                  <Save size={12} />
                                  Não salvo
                                </span>
                              )}
                              {auditedOnServer && item.status === 'divergent' && (
                                <span className="incoming-audit-status-badge incoming-audit-status-badge--divergent">
                                  <AlertTriangle size={12} />
                                  Divergente
                                </span>
                              )}
                            </div>
                            {item.product_sku && (
                              <span className="incoming-audit-product-sku">
                                SKU: {item.product_sku}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {item.product_barcode ? (
                            <span className="incoming-audit-barcode">
                              {item.product_barcode}
                            </span>
                          ) : (
                            <span className="incoming-audit-barcode-empty">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {canEdit ? (
                            canEditQuantities ? (
                              <div className="incoming-audit-quantity-input-wrapper">
                                <button
                                  className="incoming-audit-qty-btn"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id,
                                      String((item.counted_quantity || 0) - 1)
                                    )
                                  }
                                >
                                  <Minus size={14} />
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  className="incoming-audit-quantity-input"
                                  value={item.counted_quantity ?? ''}
                                  onChange={(e) =>
                                    handleQuantityChange(item.id, e.target.value)
                                  }
                                  onWheel={(e) => e.currentTarget.blur()}
                                  placeholder="0"
                                />
                                <button
                                  className="incoming-audit-qty-btn"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id,
                                      String((item.counted_quantity || 0) + 1)
                                    )
                                  }
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                            ) : (
                              <span
                                className="incoming-audit-quantity-badge incoming-audit-quantity-disabled"
                                title="Apenas o responsável pela auditoria pode adicionar quantidades"
                              >
                                {item.counted_quantity ?? '-'}
                                <br />
                                <span
                                  style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'normal',
                                  }}
                                >
                                  Apenas responsável
                                </span>
                              </span>
                            )
                          ) : canStart ? (
                            <span className="incoming-audit-quantity-badge incoming-audit-quantity-disabled">
                              Inicie a auditoria
                            </span>
                          ) : (
                            <span className="incoming-audit-quantity-badge">
                              {item.counted_quantity ?? '-'}
                            </span>
                          )}
                        </td>
                        <td className="text-right">
                          {canEditQuantities && isCounted && (
                            <button
                              className="incoming-audit-save-btn"
                              onClick={() => handleSaveItem(item)}
                              disabled={saving}
                            >
                              {saving ? (
                                <Loader2
                                  size={14}
                                  className="incoming-audit-spinner"
                                />
                              ) : (
                                <Save size={14} />
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast.show && (
        <div className={`incoming-audit-toast incoming-audit-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <MobileBarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        title="Buscar produto na auditoria"
      />

      {isMobileView && (canEdit || canStart) && (
        <button
          type="button"
          className="incoming-audit-scan-fab"
          onClick={() => setScannerOpen(true)}
          title="Escanear código de barras"
          aria-label="Escanear código de barras"
        >
          <Camera size={22} />
        </button>
      )}
    </div>
  )
}

export default IncomingAuditPage
